import {Stack} from "@src/CommonComponents/Stack/Stack";
import styles from "./leaveRequests.module.less";

import React, {useCallback, useEffect, useState} from "react";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import {PalmIcon} from "@UI/components/icons/PalmIcon.tsx";
import {HeartIcon} from "@UI/components/icons/HeartIcon.tsx";
import {BookOpen} from "@UI/components/icons/BookOpen.tsx";
import {Sun} from "@UI/components/icons/Sun.tsx";
import cn from "classnames";
import {useNavigate} from "react-router-dom";
import {useMutation, useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {apiClient} from "@src/httpClient/api.ts";
import {
    VacationRequest,
    VacationRequestRequest,
    LeaveTypeEnum,
    RequestStatusEnum,
} from "@src/httpClient/__generated__/Api.ts";
import {queryClient} from "@src/QueryClient/queryClient.ts";
import {useParams} from "react-router";
import {useEntityQuery} from "@src/httpClient/utils/queryHelpers.ts";
import {Body_14_Coral_Empasis_14, Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import {TextInput} from "@src/CommonComponents/Input/TextInput";
import {Textarea} from "@src/CommonComponents/Input/Textarea.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {AvatarWithInfo} from "@src/Pages/SettingsPage/MainMenu.tsx";
import {Checked} from "@UI/components/icons/Checked.tsx";
import {Trash} from "@UI/components/icons/Trash.tsx";
import {Xcross} from "@UI/components/icons/Xcross.tsx";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {
    ScrollableFilterToggle
} from "@src/CommonComponents/FilterSelect/ScrollableFilterToggle/ScrollableFilterToggle.tsx";
import {UserGroups} from "@UI/components/icons/UserGroups.tsx";
import {UserCard} from "@UI/components/icons/BellRinging.tsx";
import {BackButtonRow, } from "@src/CommonComponents/HeaderWithBackButton/HeaderWithBackButton.tsx";
import {useRequestAction} from "../useRequestAction.ts";
import {getApiErrorMessage} from "@src/httpClient/utils/apiError.ts";

const vacationTypeLabels: Record<LeaveTypeEnum, string> = {
    [LeaveTypeEnum.Paid]: 'Paid leave',
    [LeaveTypeEnum.Unpaid]: 'Unpaid leave',
    [LeaveTypeEnum.Sick]: 'Sick leave',
    [LeaveTypeEnum.FamilyCare]: 'Family care',
    [LeaveTypeEnum.Maternity]: 'Maternity',
    [LeaveTypeEnum.Parental]: 'Parental',
    [LeaveTypeEnum.Study]: 'Study leave',
    [LeaveTypeEnum.Compensatory]: 'Compensatory',
    [LeaveTypeEnum.Bereavement]: 'Bereavement',
    [LeaveTypeEnum.Other]: 'Other',
}

const vacationTypeIcons: Partial<Record<LeaveTypeEnum, React.ReactNode>> = {
    [LeaveTypeEnum.Paid]: <PalmIcon/>,
    [LeaveTypeEnum.Sick]: <HeartIcon/>,
    [LeaveTypeEnum.Study]: <BookOpen/>,
    [LeaveTypeEnum.Compensatory]: <Sun/>,
    [LeaveTypeEnum.FamilyCare]: <UserGroups/>,
    [LeaveTypeEnum.Parental]: <UserGroups/>,
    [LeaveTypeEnum.Maternity]: <UserGroups/>,
    [LeaveTypeEnum.Bereavement]: <UserGroups/>,
    [LeaveTypeEnum.Unpaid]: <UserCard/>,
}

const VacationTypeSelector = ({value, onChange, disabled}: {
    value: LeaveTypeEnum,
    onChange: (v: LeaveTypeEnum) => void,
    disabled?: boolean,
}) => {
    return (
        <div className={styles.typeSelector}>
            {Object.values(LeaveTypeEnum).map(type => (
                <Button
                    key={type}
                    type={value === type ? ButtonType.secondary : ButtonType.third}
                    className={cn(styles.typeChip, {[styles.typeChipActive]: value === type})}
                    onClick={() => !disabled && onChange(type)}
                    disabled={disabled}
                >
                    <Stack gap={4} alignItems={"center"}>
                        {vacationTypeIcons[type]}
                        <Gt>{vacationTypeLabels[type]}</Gt>
                    </Stack>
                </Button>
            ))}
        </div>
    )
}

interface FormState {
    date_from: string
    date_to: string
    reason: string
}

const emptyForm: FormState = {
    date_from: '',
    date_to: '',
    reason: '',
}

export const LeaveRequest = () => {
    const {requestId} = useParams<{ requestId: string }>()
    const navigate = useNavigate()
    const employee = useCredentialsStore(x => x.employee)

    const {t} = useTranslation()
    const setAppError = useErrorStore(x => x.setAppError)
    const isViewMode = !!requestId
    const isManager = !!employee?.is_manager
    const [rejectReason, setRejectReason] = useState('')
    const [vacationType, setVacationType] = useState<LeaveTypeEnum>(LeaveTypeEnum.Paid)
    const [form, setForm] = useState<FormState>(emptyForm)
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Load existing request
    const {data} = useEntityQuery<VacationRequest>(
        ["leaveRequest", requestId],
        async () => await apiClient.api.workflowVacationRequestsRetrieve(Number(requestId))
    )

    const isOwnRequest = !isViewMode || (data?.employee === employee?.id)
    const isManagerReview = isViewMode && isManager && !isOwnRequest

    // Leave left for the request's employee. An empty list means the balance
    // was never set up, and leave can't be filed until staff resolve it.
    const balanceEmployeeId = isViewMode ? data?.employee : employee?.id
    const {data: balances} = useQuery({
        queryKey: ["leave-balance", balanceEmployeeId],
        queryFn: async () => (await apiClient.api.workflowEmployeesLeaveBalanceList(balanceEmployeeId!)).data,
        enabled: !!balanceEmployeeId,
    })
    const noLedger = balances?.length === 0
    const balance = balances?.find(b => b.leave_type === vacationType)

    // Populate form from loaded data
    useEffect(() => {
        if (!data) return
        setVacationType(data.vacation_type ?? LeaveTypeEnum.Paid)
        setForm({
            date_from: data.date_from ?? '',
            date_to: data.date_to ?? '',
            reason: data.reason ?? '',
        })
    }, [data])

    const onChange = useCallback((field: keyof FormState, value: string) => {
        setForm(prev => ({...prev, [field]: value}))
        setErrors(prev => {
            const next = {...prev}
            delete next[field]
            return next
        })
    }, [])

    const validate = (): boolean => {
        const e: Record<string, string> = {}
        if (!form.date_from) e.date_from = 'Required'
        if (!form.date_to) e.date_to = 'Required'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const {mutate, isPending} = useMutation<VacationRequest, AxiosError>({
        mutationKey: ["leaveRequest", "create"],
        mutationFn: async () => {
            const payload: VacationRequestRequest = {
                employee: employee!.id,
                date_from: form.date_from,
                date_to: form.date_to,
                vacation_type: vacationType,
                reason: form.reason || undefined,
            }
            const result = await apiClient.api.workflowVacationRequestsCreate(payload)
            return result.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["leaveRequest"]})
            queryClient.invalidateQueries({queryKey: ["requests-list"]})
            navigate("/requests/?tab=leave")
        },
        onError: (error: AxiosError) => {
            setAppError({
                axiosError: error,
                message: getApiErrorMessage(error),
                showModal: true,
                retry: async () => mutate(),
            })
        },
    })

    const {actionInFlight, runAction} = useRequestAction("leaveRequest", "/requests/?tab=leave")

    const isFormValid = !!form.date_from && !!form.date_to

    const handleSubmit = useCallback(() => {
        if (!validate()) return
        mutate()
    }, [form, vacationType])

    return (
        <Stack direction={"column"} gap={16} className={styles.subMenuPage}>
            <BackButtonRow title={"Back"} backLink={"/requests/?tab=leave"} hideOnMobile/>
            <AvatarWithInfo employee={isViewMode && data ? {id: data.employee, first_name: data.employee_name} as any : employee}/>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <ScrollableFilterToggle>
                    <VacationTypeSelector
                        value={vacationType}
                        onChange={setVacationType}
                        disabled={isViewMode}
                    />
                </ScrollableFilterToggle>
                {!isViewMode && noLedger && (
                    <Body_14_Coral_Primary_Regular_14 style={{color: 'var(--coral-special-error-red)'}}>
                        <Gt>Leave balance is not set up yet. Please contact an administrator.</Gt>
                    </Body_14_Coral_Primary_Regular_14>
                )}
                {balance && (
                    <Body_14_Coral_Primary_Regular_14>
                        {t('Available: {{days}} days', {days: Number(balance.available)})}
                    </Body_14_Coral_Primary_Regular_14>
                )}
            </Stack>


            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Start</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="date_from"
                        value={form.date_from}
                        onChange={v => onChange('date_from', v)}
                        type="date"
                        hasError={!!errors.date_from}
                        disabled={isViewMode}
                    />
                </Stack>

                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>End</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="date_to"
                        value={form.date_to}
                        onChange={v => onChange('date_to', v)}
                        type="date"
                        hasError={!!errors.date_to}
                        disabled={isViewMode}
                    />
                </Stack>
            </Stack>
            <Stack direction={"column"} className={styles.whiteBlock} gap={24}>
                <Body_14_Coral_Empasis_14>
                    <Gt>Comment</Gt>
                </Body_14_Coral_Empasis_14>
                <Textarea
                    name="reason"
                    initialValue={form.reason}
                    onValueChange={v => onChange('reason', v as string)}
                    disabled={isViewMode}
                />
            </Stack>

            {isViewMode && data?.status === RequestStatusEnum.Rejected && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Stack direction={"column"} gap={6}>
                        <Body_14_Coral_Empasis_14>
                            <Gt>Rejected by</Gt>
                        </Body_14_Coral_Empasis_14>
                        <Body_14_Coral_Primary_Regular_14>
                            {data.reviewed_by_name}
                        </Body_14_Coral_Primary_Regular_14>
                    </Stack>
                    {data.reject_reason && (
                        <Stack direction={"column"} gap={6}>
                            <Body_14_Coral_Empasis_14>
                                <Gt>Rejection reason</Gt>
                            </Body_14_Coral_Empasis_14>
                            <Body_14_Coral_Primary_Regular_14>
                                {data.reject_reason}
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>
                    )}
                </Stack>
            )}

            {!isViewMode && (
                <Button
                    icon={<Checked/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_GREEN}
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={isPending || !employee?.id || !isFormValid || noLedger}
                >
                    <Gt>Submit application</Gt>
                </Button>
            )}

            {isViewMode && data?.status === RequestStatusEnum.Pending && isOwnRequest && (
                <Button
                    icon={<Trash/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_FIRE}
                    className={styles.submitButton}
                    disabled={actionInFlight}
                    onClick={() => runAction(() => apiClient.api.workflowVacationRequestsCancelCreate(Number(requestId)))}
                >
                    <Gt>Cancel request</Gt>
                </Button>
            )}

            {isManagerReview && data?.status === RequestStatusEnum.Pending && (
                <>
                    <Textarea
                        name="reject_reason"
                        initialValue={rejectReason}
                        onValueChange={v => setRejectReason(v as string)}
                        placeholder={t('Rejection reason')}
                    />
                    <div className={styles.actionButtons}>
                        <Button
                            icon={<Checked/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_GREEN}
                            className={styles.actionButton}
                            disabled={actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowVacationRequestsApproveCreate(Number(requestId)))}
                        >
                            <Gt>Approve</Gt>
                        </Button>
                        <Button
                            icon={<Xcross/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_FIRE}
                            className={styles.actionButton}
                            disabled={!rejectReason || actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowVacationRequestsRejectCreate(Number(requestId), {reason: rejectReason}))}
                        >
                            <Gt>Reject</Gt>
                        </Button>
                    </div>
                </>
            )}
        </Stack>
    )
}
